import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { difference, differenceBy, sortBy } from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import {
  debounceTime,
  delay,
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  startWith,
  takeUntil,
} from 'rxjs/operators';
import { EmployeeDto } from 'src/app/shared/flight-log-api/employees/employee-client.model';
import {
  getMaintenanceSummary,
  getSumFlightHours,
  getSumPilotLandings,
} from 'src/app/shared/flight-log-api/flight-logs/flight-log-client-model-helpers';
import {
  createBillingSummary,
  createFlightLog,
  createFuelEvent,
  createLogEntry,
  createPilotLogEntry,
  createWaypoint,
  CrewOnboardDto,
  FlightAdvantage,
  FlightHoursDto,
  FlightLogEntryDto,
  LogMode,
  PilotLogEntryDto,
  WaypointDto,
} from 'src/app/shared/flight-log-api/flight-logs/flight-log-client.model';
import {
  AircraftType,
  CrewMemberDto,
  CrewRole,
  FlightType,
  isPilot,
} from 'src/app/shared/flight-log-api/shared/shared-client.model';
import { FlightLogEntity } from 'src/store/flight-log/flight-log.model';
import {
  forEachControl,
  getStatusChanges,
  getValueChanges,
  removeControlsWhere,
} from 'src/util/angular/ng-forms-helpers';
import {
  collapseValidationState,
  getValidationStateChanges,
  ValidationState,
  ValidationStateCollapsed,
} from 'src/util/angular/ng-forms-validation';
import { parseDateOrDefault } from 'src/util/dateLib';
import { uuid } from 'src/util/uuid';
import { FlightLogFeatureProps, UserViewModel } from 'src/view-models/user-view-model';
import {
  createCrewMemberFormGroup,
  createCrewOnboardFormGroup,
  createFlightAdvantagesFormArray,
  createFlightLogFormGroup,
  createFuelEventFormGroup,
  createLogEntryFormGroup,
  createPilotLogEntryFormGroup,
  createWaypointFormGroup,
} from './flight-log-form.model';

function initFlightLog(inputLog: Partial<FlightLogEntity> | null) {
  const log = createFlightLog();
  Object.assign(log, inputLog);

  if (log.entries.length === 0) {
    if (log.mode !== LogMode.EntryPerLeg) {
      log.entries.push(createLogEntry(null, null));
    } else {
      const from = createWaypoint();
      const to = createWaypoint();
      const entry = createLogEntry(from.id, to.id);
      log.waypoints.push(from, to);
      log.entries.push(entry);
    }
  } else if (log.mode !== LogMode.Simple) {
    log.entries.forEach((entry) => {
      // billing summary cannot be null.
      if (entry.entryDetail.billingSummary == null) {
        entry.entryDetail.billingSummary = createBillingSummary();
      }
      if (log.mode === LogMode.EntryPerFlight) {
        delete (entry.entryDetail as any).fromWaypointId;
        delete (entry.entryDetail as any).toWaypointId;
        entry.entryDetail.fromWaypointId = null;
        entry.entryDetail.toWaypointId = null;
        log.waypoints = [];
      }
    });
  }
  return log;
}
export class FlightLogFormBuilderContext {
  public readonly flightLog: FlightLogEntity;
  constructor(
    public readonly user: UserViewModel,
    flightLog: Partial<FlightLogEntity> | null,
    public readonly features: FlightLogFeatureProps,
    public readonly fb: UntypedFormBuilder,
  ) {
    this.flightLog = initFlightLog(flightLog);
  }
}

export function enableNvgForLeg(entry: UntypedFormGroup) {
  forEachControl<UntypedFormGroup>(
    entry.get('pilotLogEntries') as UntypedFormArray,
    (pilotLogEntry) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const nightAided = pilotLogEntry.get('flightHours.nightAided')!;
      nightAided.enable();

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const hnvgo = pilotLogEntry.get('hnvgo')!;
      hnvgo.enable();
    },
  );
}

export function disableNvgForLeg(entry: UntypedFormGroup) {
  forEachControl<UntypedFormGroup>(
    entry.get('entryDetail.crewOnboard') as UntypedFormArray,
    (memberLegInfo) => {
      memberLegInfo.patchValue({ isNvg: false, hnvgo: null });
    },
  );

  forEachControl<UntypedFormGroup>(
    entry.get('pilotLogEntries') as UntypedFormArray,
    (pilotLogEntry) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const nightAided = pilotLogEntry.get('flightHours.nightAided')!;
      nightAided.setValue(null);
      nightAided.disable();

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const hnvgo = pilotLogEntry.get('hnvgo')!;
      hnvgo.setValue(null);
      hnvgo.disable();
    },
  );
}

export function sortCrewMembers(crewMembers: CrewMemberDto[]): CrewMemberDto[] {
  return sortBy(crewMembers, ['lastName']);
}

export function isFixedWingOrUnknown(aircraftType: AircraftType | null) {
  return aircraftType != null
    ? aircraftType === AircraftType.FixedWing || aircraftType === AircraftType.Unknown
    : false;
}

export function isRotorWingOrUnknown(aircraftType: AircraftType | null) {
  return aircraftType != null
    ? aircraftType === AircraftType.RotorWing || aircraftType === AircraftType.Unknown
    : false;
}

export class FlightLogFormHelper {
  public form: UntypedFormGroup;
  public formValues$: Observable<FlightLogEntity>;
  public isTraining$: Observable<boolean>;
  public isPart133$: Observable<boolean>;
  public flightCrew$: Observable<CrewMemberDto[]>;
  public aircraftId$: Observable<string | undefined>;
  public validationState$: Observable<ValidationState>;
  public isValid$: Observable<boolean>;
  public validationStateCollapsed$: Observable<ValidationStateCollapsed>;
  private _isDisposed$ = new Subject();
  private _waypointChanges$: Subject<WaypointDto> = new Subject();
  get flight(): FlightLogEntity {
    return this.context.flightLog;
  }

  constructor(public readonly context: FlightLogFormBuilderContext) {
    if (this.flight.entries == null || this.flight.entries.length < 1) {
      throw new Error('Input flight log needs one or more log entries');
    }

    this.form = createFlightLogFormGroup(this.context);
    // Define Observables
    this.formValues$ = getValueChanges(this.form);
    const isFlightType$ = (flightType: FlightType) =>
      this.formValues$.pipe(
        map((v) => v.flightType),
        distinctUntilChanged(),
        map((ft) => ft === flightType),
      );
    this.isTraining$ = isFlightType$(FlightType.Training);
    this.isPart133$ = isFlightType$(FlightType.Part133);
    this.aircraftId$ = getValueChanges<string | undefined>(this.aircraftIdCtrl);
    this.flightCrew$ = getValueChanges(this.flightCrewControl).pipe(map(sortCrewMembers));
    this.validationState$ = getValidationStateChanges(this.form);
    this.validationStateCollapsed$ = this.validationState$.pipe(
      map((s) => collapseValidationState(s)),
    );
    this.isValid$ = getStatusChanges(this.form).pipe(map((s) => s === 'VALID'));

    forEachControl<UntypedFormGroup>(this.entries, (logEntryCtrl) => {
      this._initLogEntry(logEntryCtrl);
    });

    // Observable Logic

    // Publish any changes of pre-existing waypoints
    this.publishWaypointChanges(this.waypoints.controls);
    // Since FlightLogEntries only reference waypoints by ID,
    // we want to subscribe to any waypoint changes and also
    // trigger a change event on the flight log so validations will run
    this._waypointChanges$
      .pipe(
        // Adding a 1ms delay because for some reason the downstream validators
        // for log entries won't pick up the new waypoint values unless we do this
        // - possibly an Angular bug
        delay(1),
        takeUntil(this._isDisposed$),
      )
      .subscribe((waypoint: WaypointDto) => {
        const entriesWityPaypint = this.entries.controls.filter(
          (entryFormGroup: AbstractControl) => {
            const entry: FlightLogEntryDto = entryFormGroup.value;
            const { fromWaypointId, toWaypointId } = entry.entryDetail;
            const match = fromWaypointId === waypoint.id || toWaypointId === waypoint.id;
            return match;
          },
        );

        entriesWityPaypint.forEach((entry) =>
          // patch empty value to trigger change event
          entry.patchValue({}),
        );
      });

    if (this.maintenanceSummary != null) {
      this.formValues$
        .pipe(
          // Prevents infinite loop as we're patching the form values below.
          // We're not supressing the emitted event because we don't want to
          // prevent valueChanges from being used on these values
          debounceTime(100),
          takeUntil(this._isDisposed$),
        )
        .subscribe((flightLog: FlightLogEntity) => {
          const {
            part133HoistDayHours,
            part133HoistNightHours,
            part133HookDayHours,
            part133HookNightHours,
          } = getSumFlightHours(flightLog);
          const { dayNightSum, runOn, hnvgo } = getSumPilotLandings(flightLog);
          const ms = getMaintenanceSummary(flightLog);
          const allLandings = dayNightSum + hnvgo;
          if (this.maintenanceSummary != null && ms != null) {
            this.maintenanceSummary.patchValue({
              hook: {
                flightTime: part133HookDayHours,
                nightTime: part133HookNightHours,
              },
              hoist: {
                flightTime: part133HoistDayHours,
                nightTime: part133HoistNightHours,
              },
            });
            if (ms.totalLandings !== allLandings) {
              this.maintenanceSummary.patchValue({
                totalLandings: allLandings,
              });
            }
            if (ms.runOnLandings !== runOn) {
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              this.maintenanceSummary!.patchValue({
                runOnLandings: runOn,
              });
            }
          }
        });
    }
    getValueChanges<CrewMemberDto[]>(this.flightCrewControl)
      .pipe(takeUntil(this._isDisposed$))
      .subscribe((flightCrew) => {
        this.updateLegWithFlightCrew(flightCrew);
      });

    // triggers only when a different aircraft is selected
    this.aircraftId$
      .pipe(
        filter((id) => id != null),
        pairwise(),
        filter(([a, b]) => a?.toLowerCase() !== b?.toLowerCase()),
        takeUntil(this._isDisposed$),
      )
      .subscribe(([a, b]) => {
        this.form.controls.matowInKg?.setValue(null);
        this.form.controls.maxAftCGInInches?.setValue(null);
        this.form.controls.maxForeCGInInches?.setValue(null);
        forEachControl<UntypedFormGroup>(this.entries, (entry) => {
          entry.get('entryDetail.passengerCount')?.setValue(null);
          entry.get('entryDetail.totalLoadedWeightInKg')?.setValue(null);
          entry.get('entryDetail.actIndex')?.setValue(null);
        });
      });
  }

  get flightTime(): Date | undefined {
    let date: Date | undefined;
    if (this.isEntryPerLeg) {
      const firstLeg = this.getLogEntry(0);
      if (firstLeg == null) {
        return undefined;
      }
      const firstWaypointId = firstLeg.value.entryDetail.fromWaypointId;
      if (firstWaypointId != null) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const firstWaypoint: WaypointDto = this.getWaypointById(firstWaypointId)!.value;
        date = parseDateOrDefault(firstWaypoint.departureTime);
      }
    } else {
      const entries = this.form.get('entries') as UntypedFormArray;
      const entry = entries.get([0]) as AbstractControl;
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      date = parseDateOrDefault(entry.get('flightTime')!.value);
    }
    return date;
  }

  get flightTimeCtrl(): UntypedFormControl | undefined {
    let ctrl: UntypedFormControl | undefined;

    if (this.isEntryPerLeg) {
      const firstLeg = this.getLogEntry(0);
      if (firstLeg == null) {
        ctrl = undefined;
      }

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ctrl = firstLeg!.get('flightTime')! as UntypedFormControl;
    } else {
      const entries = this.form.get('entries') as UntypedFormArray;
      const entry = entries.get([0]) as AbstractControl;
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ctrl = entry.get('flightTime')! as UntypedFormControl;
    }
    return ctrl;
  }

  get aircraftIdCtrl(): UntypedFormControl {
    return this.form.get('aircraftId') as UntypedFormControl;
  }

  get maintenanceSummary() {
    return this.form.get('maintenanceSummary');
  }

  public updateLegWithFlightCrew(flightCrew: CrewMemberDto[]) {
    forEachControl<UntypedFormGroup>(this.entries, (entry) => {
      const crewOnboard = entry.get('entryDetail.crewOnboard') as UntypedFormArray;

      // map to an object with an ID property so we can compare with differenceBy
      const crewOnboardIds: { id: string }[] = crewOnboard.value.map((c: CrewOnboardDto) => {
        return { id: c.flightCrewId };
      });
      const removedCrewIds = differenceBy(crewOnboardIds, flightCrew, (c) => c.id).map((c) => c.id);

      removeControlsWhere(
        crewOnboard,
        (crew) =>
          removedCrewIds.includes(crew.value.flightCrewId) || crew.value.flightCrewId === null,
      );
    });
  }

  public resetEntries() {
    if (!this.isEntryPerLeg) {
      throw new Error('Resetting log entries is only supported for entry per leg.');
    }
    while (this.entries.length > 0) {
      this.removeLogEntry(0);
    }
    this.addLogEntry(0);
  }

  get flightCrewControl(): UntypedFormArray {
    return this.form.get('flightCrew') as UntypedFormArray;
  }

  get isEntryPerLeg() {
    return this.flight.mode === LogMode.EntryPerLeg;
  }

  get isEntryPerFlight() {
    return this.flight.mode === LogMode.EntryPerFlight;
  }

  get isSimple() {
    return this.flight.mode === LogMode.Simple;
  }

  get flightCrew(): CrewMemberDto[] {
    return this.form.value.flightCrew;
  }

  set advantages(advantages: FlightAdvantage[]) {
    const advantagesControl = createFlightAdvantagesFormArray(this.context, advantages);
    this.form.setControl('advantages', advantagesControl);
  }

  public addLogEntry(index: number) {
    if (!this.isEntryPerLeg) {
      throw new Error('Adding log entries is only supported for entry-per-leg logs');
    }

    const newDestinationWaypoint = createWaypoint();
    const prev: UntypedFormGroup | null = this.getLogEntryDetails(index - 1);
    const futureNext: UntypedFormGroup | null = this.getLogEntryDetails(index);

    if (prev == null && futureNext != null) {
      this.removeWaypointWithId(futureNext.value.fromWaypointId);
    }

    const fromWaypoint =
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      prev != null ? this.getWaypointById(prev.value.toWaypointId)!.value : createWaypoint();

    if (futureNext != null) {
      futureNext.patchValue({ fromWaypointId: newDestinationWaypoint.id });
    }

    const entry = createLogEntry(fromWaypoint.id, newDestinationWaypoint.id);
    const entryControlGroup = createLogEntryFormGroup(this.context)(entry);
    this._initLogEntry(entryControlGroup);
    this.entries.insert(index, entryControlGroup);
    this.addWaypoint(newDestinationWaypoint);
    if (prev == null) {
      this.addWaypoint(fromWaypoint);
    }
  }

  private _initLogEntry(entry: UntypedFormGroup) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    getValueChanges(entry.get('entryDetail.crewOnboard')!)
      .pipe(takeUntil(this._isDisposed$))
      .subscribe((crew: CrewOnboardDto[]) => {
        const pilotLogEntries = entry.get('pilotLogEntries') as UntypedFormArray;
        const pilotLogEntryCrewIds = pilotLogEntries.value.map(
          (c: PilotLogEntryDto) => c.flightCrewId,
        );
        const onBoardPilots = crew.filter((c) => this.isPilot(c)).map((c) => c.flightCrewId);
        const newlyOnboardPilotIds = difference(onBoardPilots, pilotLogEntryCrewIds);

        removeControlsWhere(pilotLogEntries, (c) => {
          const flightCrewId = c.value.flightCrewId;
          const onBoard = onBoardPilots.includes(flightCrewId);
          return !onBoard;
        });

        newlyOnboardPilotIds.forEach((flightCrewId) => {
          const pilotLogEntry = createPilotLogEntry(flightCrewId);
          const pilotLogEntryFormGroup = createPilotLogEntryFormGroup(this.context)(pilotLogEntry);
          this._initPilotLogEntryCtrl(pilotLogEntryFormGroup);
          pilotLogEntries.push(pilotLogEntryFormGroup);
        });
      });

    const pilotLogEntriesCtrl = entry.get('pilotLogEntries') as UntypedFormArray;
    forEachControl<UntypedFormGroup>(pilotLogEntriesCtrl, (pilotLogEntryCtrl) => {
      this._initPilotLogEntryCtrl(pilotLogEntryCtrl);
    });
  }

  private _initPilotLogEntryCtrl(pilotLogEntryCtrl: UntypedFormGroup) {
    const flightHoursCtrl = pilotLogEntryCtrl.get('flightHours') as UntypedFormGroup;
    const part133$ = getValueChanges<FlightHoursDto>(flightHoursCtrl).pipe(
      map(
        ({
          part133HoistDayHours,
          part133HookDayHours,
          part133HoistNightHours,
          part133HookNightHours,
        }) => {
          const part133 =
            part133HoistDayHours +
            part133HoistNightHours +
            part133HookDayHours +
            part133HookNightHours;
          return part133;
        },
      ),
      distinctUntilChanged(),
    );
    combineLatest([this.isPart133$, part133$])
      .pipe(takeUntil(this._isDisposed$))
      .subscribe(([isPart133, part133]) => {
        if (isPart133) {
          flightHoursCtrl.patchValue({ part133 });
        } else {
          flightHoursCtrl.patchValue({
            part133: 0,
            part133HoistDayHours: 0,
            part133HoistNightHours: 0,
            part133HookDayHours: 0,
            part133HookNightHours: 0,
          });
        }
      });
  }

  private isPilot(crewOnboard: CrewOnboardDto) {
    return (
      this.flightCrew.findIndex((c) => c.id === crewOnboard.flightCrewId && isPilot(c.role)) !== -1
    );
  }

  public addWaypoint(waypoint: WaypointDto) {
    const waypointFormGroup = createWaypointFormGroup(this.context)(waypoint);
    this.publishWaypointChanges([waypointFormGroup]);
    this.waypoints.insert(this.waypoints.length, waypointFormGroup);
  }

  /**
   * Used to subscribe any changes to waypoints to a subject for downstream
   * listening by FlightLogEntry FormGroups.
   *
   */
  private publishWaypointChanges(waypointFormGroups: AbstractControl[]) {
    waypointFormGroups.forEach((fg) => {
      fg.valueChanges
        .pipe(takeUntil(this._isDisposed$))
        .subscribe((w: WaypointDto) => this._waypointChanges$.next(w));
    });
  }

  public addCrewMemberToLeg(entry: UntypedFormGroup, flightCrewId: string) {
    const crewOnboard = entry.get('entryDetail.crewOnboard') as UntypedFormArray;
    crewOnboard.push(
      createCrewOnboardFormGroup(this.context)({
        flightCrewId,
        isNvg: false,
        hnvgo: null,
      }),
    );
  }

  public addCrewMemberToFlight(employee: EmployeeDto) {
    const flightCrewId = uuid();
    const crwMember = createCrewMemberFormGroup(this.context)({
      id: flightCrewId,
      employeeId: employee.id,
      firstName: employee.firstName,
      lastName: employee.lastName,
      role: employee.role,
    });
    this.flightCrewControl.push(crwMember);
    forEachControl(this.entries, (entry: UntypedFormGroup) => {
      this.addCrewMemberToLeg(entry, flightCrewId);
    });
  }

  get entries(): UntypedFormArray {
    return this.form.get('entries') as UntypedFormArray;
  }

  get waypoints(): UntypedFormArray {
    return this.form.get('waypoints') as UntypedFormArray;
  }

  public isLastLogEntry(index: number) {
    return this.entries.length === index + 1;
  }

  public getLastLogEntryCtrl(): UntypedFormGroup | null {
    return this.entries.get([this.entries.length - 1]) as UntypedFormGroup | null;
  }

  public getLastLogEntry(): FlightLogEntryDto | null {
    const lastLogEntryCtrl = this.getLastLogEntryCtrl();
    if (lastLogEntryCtrl != null) {
      return lastLogEntryCtrl.value;
    }
    return null;
  }

  public getWaypointByIndex(index: number): UntypedFormGroup {
    // angular v14+ no longer returns null on negative indexes. (see: https://github.com/angular/angular/issues/44642)
    return (index < 0 ? null : this.waypoints.get([index])) as UntypedFormGroup;
  }

  public addFuelEvent(entry: UntypedFormGroup) {
    const crewOnboard = entry.get('entryDetail.fuelEvents') as UntypedFormArray;
    crewOnboard.push(createFuelEventFormGroup(this.context)(createFuelEvent()));
  }

  public removeCrewMemberFromLeg(entry: UntypedFormGroup, flightCrewId: string) {
    const crewOnboard = entry.get('entryDetail.crewOnboard') as UntypedFormArray;
    for (let i = 0; i < crewOnboard.length; i++) {
      const crewLeg = crewOnboard.get([i]);
      if (crewLeg != null && crewLeg.value.flightCrewId === flightCrewId) {
        crewOnboard.removeAt(i);
        return;
      }
    }
  }

  public getToWaypointForEntry$(index: number): Observable<UntypedFormGroup> {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const entryDetails = this.getLogEntryDetails(index)!;

    return entryDetails.valueChanges.pipe(
      map((v) => v.toWaypointId),
      distinctUntilChanged(),
      startWith(entryDetails.value.toWaypointId),
      map((toWaypointId) => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return this.getWaypointById(toWaypointId)!;
      }),
    );
  }

  public getFromWaypointForEntry$(index: number): Observable<UntypedFormGroup> {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const entryDetails = this.getLogEntryDetails(index)!;
    return entryDetails.valueChanges.pipe(
      map((e) => e.fromWaypointId),
      distinctUntilChanged(),
      startWith(entryDetails.value.fromWaypointId),
      map((fromWaypointId) => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return this.getWaypointById(fromWaypointId)!;
      }),
    );
  }

  public getWaypointById(id: string): UntypedFormGroup | null {
    return id != null ? this.forWaypointWithId(id, (waypoint) => waypoint) : null;
  }

  public forWaypointWithId(
    id: string,
    action: (waypoint: UntypedFormGroup, index: number) => any | void,
  ) {
    for (let i = 0; i < this.waypoints.length; i++) {
      const waypoint = this.getWaypointByIndex(i);
      if (waypoint.value.id === id) {
        return action(waypoint, i);
      }
    }
  }

  get pilots$(): Observable<CrewMemberDto[]> {
    return this.flightCrew$.pipe(
      map((crew) => crew.filter((c) => c.role === CrewRole.PilotInCommand)),
    );
  }

  public getLogEntry(index: number): UntypedFormGroup | null {
    // angular v14+ no longer returns null on negative indexes. (see: https://github.com/angular/angular/issues/44642)
    return (index < 0 ? null : this.entries.get([index])) as UntypedFormGroup | null;
  }

  public getLogEntryDetails(index: number): UntypedFormGroup | null {
    const logEntry = this.getLogEntry(index);
    if (logEntry != null) {
      return logEntry.get('entryDetail') as UntypedFormGroup;
    }
    return null;
  }

  public canRemoveLogEntry(index: number): boolean {
    // return this.entries.length > 1 &&
    return index < this.entries.length && index >= 0;
  }

  public removeLogEntry(index: number) {
    if (!this.canRemoveLogEntry(index)) {
      throw new Error('Cannot remove the first log entry or a log entry that does not exist');
    }
    const prevEntryDetail = this.getLogEntryDetails(index - 1);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const thisEntryDetail = this.getLogEntryDetails(index)!;
    const nextEntryDetail = this.getLogEntryDetails(index + 1);
    const toWaypointId = thisEntryDetail.value.toWaypointId;
    const fromWaypointId = thisEntryDetail.value.fromWaypointId;

    if (prevEntryDetail != null && nextEntryDetail != null) {
      nextEntryDetail.patchValue({
        fromWaypointId: prevEntryDetail.value.toWaypointId,
      });
    }
    this.entries.removeAt(index);

    // Remove waypoints
    if (prevEntryDetail == null) {
      this.removeWaypointWithId(fromWaypointId);
    }
    this.removeWaypointWithId(toWaypointId);

    // Ensure last log entry's departure time is set to null
    const lastLogEntry = this.getLastLogEntry();
    if (lastLogEntry != null && lastLogEntry.entryDetail.toWaypointId != null) {
      this.forWaypointWithId(lastLogEntry.entryDetail.toWaypointId, (w) => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        w.get('departureTime')!.setValue(null);
      });
    }
  }

  public removeWaypointWithId(id: string) {
    this.forWaypointWithId(id, (w, i) => {
      this.waypoints.removeAt(i);
    });
  }

  public dispose() {
    this._isDisposed$.complete();
  }
}
